import { Typography } from '@remarkable/ark-web';
import type { FleetDevice } from 'src/api/endpoints/cloudEnterprise.types';
import { useSendCommandSoftwareForceRestart } from 'src/api/queries/enterprisePolicies';
import { Button, Modal, NotificationBox } from 'src/components';
import { DeviceIcon } from 'src/components/DeviceIcon';
import { DeviceName } from 'src/components/DeviceName';

type DeviceActionSoftwareForceRestartProps = {
  device: FleetDevice;
  userEmail: string | undefined;
  userId: string;
  onSuccess?: () => void;
};

export function DeviceActionSoftwareForceRestart({
  device,
  userEmail,
  userId,
  onSuccess,
}: DeviceActionSoftwareForceRestartProps) {
  const sendCommandSoftwareForceRestart = useSendCommandSoftwareForceRestart();

  return (
    <Modal.Content
      title="Enable software force restart?"
      className="flex flex-col gap-24"
    >
      <div>
        <Typography variant="body-lg" fontWeight="semibold" as="div">
          <DeviceIcon
            device={device}
            size={24}
            className="mr-6 inline align-sub"
          />
          <b>
            <DeviceName device={device} />
          </b>{' '}
          / <span className="whitespace-nowrap">{device.serialNumber}</span>
        </Typography>

        <Typography variant="body-lg" className="mt-8" as="div">
          (paired with {userEmail})
        </Typography>
      </div>

      <Modal.Description asChild>
        <Typography variant="body-md">
          This will force the device to restart and apply any pending software
          updates. Restart will occur after 4 hours of inactivity.
        </Typography>
      </Modal.Description>

      <div className="flex justify-center gap-12">
        <Modal.Close asChild>
          <Button variant="secondary">Close</Button>
        </Modal.Close>

        <Button
          loading={
            sendCommandSoftwareForceRestart.isPending &&
            sendCommandSoftwareForceRestart.variables?.enable === false
          }
          onClick={() => {
            sendCommandSoftwareForceRestart.mutate(
              { deviceId: device.serialNumber, userId, enable: false },
              { onSuccess }
            );
          }}
        >
          Disable
        </Button>

        <Button
          variant="primary"
          loading={
            sendCommandSoftwareForceRestart.isPending &&
            sendCommandSoftwareForceRestart.variables?.enable === true
          }
          onClick={() => {
            sendCommandSoftwareForceRestart.mutate(
              { deviceId: device.serialNumber, userId, enable: true },
              { onSuccess }
            );
          }}
        >
          Enable
        </Button>
      </div>

      <NotificationBox error={sendCommandSoftwareForceRestart.error} />
    </Modal.Content>
  );
}
